import React, { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ModuleHeader } from 'components/modals';
import type { ActionButton, ViewModeOption } from 'components/modals';
import { SyutGrid, type SyutGridColumn } from 'components/ui';
import { useSnackbar } from 'components/common/SnackbarNotification';
import { formatCurrency, getStatusBadgeStyle } from 'utils/lmmsHelpers';
import { useLoanMappingsPaged } from 'hooks/api';
import { GridLoadingSkeleton } from 'components/common';
import type { LoanMapping, MappedGL, LoanMappingFilterParams, LoanMappingReportFilterParams } from 'api/types/loanMapping.types';
import { loanMappingService } from 'api/services/loanMappingService';

// ============================================================================
// TYPES
// ============================================================================

interface MappingRow {
  id: string;
  branchDisplay: string;
  businessUnitCode: string;
  businessUnitName: string;
  customerId: string;
  customerName: string;
  addonLoanType: string;
  addonLoanAccountNumber: string;
  addonLoanDate: string;
  approvedAmount: number;
  statusCode: string;
  approvedAt: string;
  createdAt: string;
  glCount: number;
  glNumbers: string;
  glCustomerIds: string;
  glCustomerNames: string;
  totalNetWeight: number;
  totalGoldLoanAmount: number;
}

function toMappingRow(m: LoanMapping): MappingRow {
  const code = m.businessUnitCode ?? '';
  const name = m.businessUnitName ?? '';
  const branchDisplay = code && name ? `${code} – ${name}` : name || code;
  return {
    id: String(m.id),
    branchDisplay,
    businessUnitCode: code,
    businessUnitName: name,
    customerId: m.customerId ?? '',
    customerName: m.customerName ?? '',
    addonLoanType: m.addonLoanType ?? '',
    addonLoanAccountNumber: m.addonLoanAccountNumber ?? '',
    addonLoanDate: m.addonLoanDate ?? '',
    approvedAmount: m.approvedAmount,
    statusCode: m.statusCode ?? '',
    approvedAt: m.approvedAt ?? '',
    createdAt: m.createdAt ?? '',
    glCount: m.glCount ?? 0,
    glNumbers: m.glNumbers ?? '',
    glCustomerIds: m.glCustomerIds ?? '',
    glCustomerNames: m.glCustomerNames ?? '',
    totalNetWeight: m.totalNetWeight ?? 0,
    totalGoldLoanAmount: m.totalGoldLoanAmount ?? 0,
  };
}

function fmtDate(val?: string | null): string {
  if (!val) return '';
  return val.split('T')[0];
}

const DEFAULT_PAGE_SIZE = 25;

// ============================================================================
// MAIN SCREEN
// ============================================================================

const LoanMappingReportScreen: React.FC = () => {
  const { showMessage } = useSnackbar();

  // ── Applied server-side query (page + filters). The grid and the export both
  //    honour exactly these criteria — no client-side slicing anywhere. ────────
  const [queryParams, setQueryParams] = useState<LoanMappingFilterParams>({
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  const { data, pagination, loading: apiLoading, error: apiError, refetch } = useLoanMappingsPaged(queryParams);

  const [viewMode, setViewMode] = useState<string>('list');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);

  // Draft filter inputs (applied to queryParams on "Apply")
  const [filterStatus, setFilterStatus] = useState('');
  const [filterBranch, setFilterBranch] = useState('');
  const [filterCustomerId, setFilterCustomerId] = useState('');
  const [filterLoanAccount, setFilterLoanAccount] = useState('');
  const [filterGlNumber, setFilterGlNumber] = useState('');
  const [filterGlCustomerId, setFilterGlCustomerId] = useState('');
  const [filterGlCustomerName, setFilterGlCustomerName] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // GL detail panel
  const [expandedMappingId, setExpandedMappingId] = useState<string | null>(null);
  const glCacheRef = useRef<Map<string, MappedGL[]>>(new Map());
  const [glPanelRows, setGlPanelRows] = useState<MappedGL[]>([]);
  const [glPanelLoading, setGlPanelLoading] = useState(false);

  // Export dropdown
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const [exportSummaryLoading, setExportSummaryLoading] = useState(false);
  const [exportDetailLoading, setExportDetailLoading] = useState(false);
  const exportMenuRef = useRef<HTMLDivElement>(null);

  // Close export menu on outside click
  useEffect(() => {
    if (!exportMenuOpen) return;
    const handler = (e: MouseEvent) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(e.target as Node)) {
        setExportMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [exportMenuOpen]);

  // ── Debounced search → applied query (server-side) ────────────────────────
  useEffect(() => {
    const timer = setTimeout(() => {
      setQueryParams((prev) => {
        const next = searchQuery || undefined;
        if (prev.searchTerm === next) return prev;
        return { ...prev, searchTerm: next, page: 1 };
      });
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // ============================================================================
  // ROWS (current page only — server already sliced + filtered)
  // ============================================================================

  const mappingRows = useMemo(() => data.map(toMappingRow), [data]);

  const isFilterActive = !!(
    queryParams.statusCode || queryParams.branch || queryParams.customerId ||
    queryParams.loanAccount || queryParams.glNumber || queryParams.glCustomerId ||
    queryParams.glCustomerName || queryParams.dateFrom || queryParams.dateTo
  );

  // ── Pagination handlers (server-side) ─────────────────────────────────────
  const handlePageChange = useCallback((pageIndex: number) => {
    setQueryParams((prev) => ({ ...prev, page: pageIndex + 1 })); // API is 1-based
  }, []);

  const handlePageSizeChange = useCallback((size: number) => {
    setQueryParams((prev) => ({ ...prev, pageSize: size, page: 1 }));
  }, []);

  // ── Apply / Clear filters ─────────────────────────────────────────────────
  const handleApplyFilters = useCallback(() => {
    setQueryParams((prev) => ({
      ...prev,
      page: 1,
      statusCode: filterStatus || undefined,
      branch: filterBranch || undefined,
      customerId: filterCustomerId || undefined,
      loanAccount: filterLoanAccount || undefined,
      glNumber: filterGlNumber || undefined,
      glCustomerId: filterGlCustomerId || undefined,
      glCustomerName: filterGlCustomerName || undefined,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
    }));
    setFilterOpen(false);
  }, [filterStatus, filterBranch, filterCustomerId, filterLoanAccount, filterGlNumber, filterGlCustomerId, filterGlCustomerName, dateFrom, dateTo]);

  const handleClearFilters = useCallback(() => {
    setFilterStatus('');
    setFilterBranch('');
    setFilterCustomerId('');
    setFilterLoanAccount('');
    setFilterGlNumber('');
    setFilterGlCustomerId('');
    setFilterGlCustomerName('');
    setDateFrom('');
    setDateTo('');
    setQueryParams((prev) => ({
      page: 1,
      pageSize: prev.pageSize,
      searchTerm: prev.searchTerm,
    }));
  }, []);

  // ── Map the applied query into the export param + banner filter labels ─────
  const buildReportFilters = useCallback((): LoanMappingReportFilterParams => ({
    statusCode: queryParams.statusCode,
    customerId: queryParams.customerId,
    searchTerm: queryParams.searchTerm,
    branch: queryParams.branch,
    loanAccount: queryParams.loanAccount,
    glNumber: queryParams.glNumber,
    glCustomerId: queryParams.glCustomerId,
    glCustomerName: queryParams.glCustomerName,
    dateFrom: queryParams.dateFrom,
    dateTo: queryParams.dateTo,
  }), [queryParams]);

  const buildAppliedFilterLabels = useCallback((): string[] => {
    const list: string[] = [];
    if (queryParams.branch)         list.push(`Branch: ${queryParams.branch}`);
    if (queryParams.customerId)     list.push(`Customer ID: ${queryParams.customerId}`);
    if (queryParams.loanAccount)    list.push(`Loan Account: ${queryParams.loanAccount}`);
    if (queryParams.glNumber)       list.push(`GL Account No.: ${queryParams.glNumber}`);
    if (queryParams.glCustomerId)   list.push(`GL Customer ID: ${queryParams.glCustomerId}`);
    if (queryParams.glCustomerName) list.push(`GL Customer Name: ${queryParams.glCustomerName}`);
    if (queryParams.statusCode)     list.push(`Status: ${queryParams.statusCode.replace(/_/g, ' ')}`);
    if (queryParams.dateFrom)       list.push(`Date From: ${queryParams.dateFrom}`);
    if (queryParams.dateTo)         list.push(`Date To: ${queryParams.dateTo}`);
    if (queryParams.searchTerm)     list.push(`Search: ${queryParams.searchTerm}`);
    return list;
  }, [queryParams]);

  // ============================================================================
  // GL COUNT CLICK — lazy-load + cache
  // ============================================================================

  const handleGlCountClick = useCallback(async (mappingId: string) => {
    if (expandedMappingId === mappingId) {
      setExpandedMappingId(null);
      return;
    }
    setExpandedMappingId(mappingId);
    if (glCacheRef.current.has(mappingId)) {
      setGlPanelRows(glCacheRef.current.get(mappingId)!);
      return;
    }
    setGlPanelLoading(true);
    try {
      const res = await loanMappingService.getById(mappingId);
      const gls = res.data?.mappedGls ?? [];
      glCacheRef.current.set(mappingId, gls);
      setGlPanelRows(gls);
    } catch {
      showMessage('Failed to load GL details.', 'error');
      setExpandedMappingId(null);
    } finally {
      setGlPanelLoading(false);
    }
  }, [expandedMappingId, showMessage]);

  // ============================================================================
  // EXPORT (server-side — backend builds the styled .xlsx and streams a blob)
  // ============================================================================

  const handleExportSummary = useCallback(async () => {
    setExportMenuOpen(false);
    if (pagination.totalItems === 0) { showMessage('No data to export.', 'info'); return; }
    setExportSummaryLoading(true);
    showMessage('Preparing Summary export…', 'info');
    try {
      await loanMappingService.downloadReport(
        { ...buildReportFilters(), format: 'summary' },
        buildAppliedFilterLabels(),
      );
      showMessage('Summary export downloaded.', 'success');
    } catch {
      showMessage('Summary export failed. Please try again.', 'error');
    } finally {
      setExportSummaryLoading(false);
    }
  }, [pagination.totalItems, showMessage, buildReportFilters, buildAppliedFilterLabels]);

  const handleExportDetail = useCallback(async () => {
    setExportMenuOpen(false);
    if (pagination.totalItems === 0) { showMessage('No data to export.', 'info'); return; }
    setExportDetailLoading(true);
    showMessage('Preparing Detail export…', 'info');
    try {
      await loanMappingService.downloadReport(
        { ...buildReportFilters(), format: 'detail' },
        buildAppliedFilterLabels(),
      );
      showMessage('Detail export downloaded.', 'success');
    } catch {
      showMessage('Detail export failed. Please try again.', 'error');
    } finally {
      setExportDetailLoading(false);
    }
  }, [pagination.totalItems, showMessage, buildReportFilters, buildAppliedFilterLabels]);

  // ============================================================================
  // COLUMN DEFINITIONS
  // ============================================================================

  const tableColumns: SyutGridColumn<MappingRow>[] = useMemo(() => [
    {
      id: 'branchDisplay',
      header: 'Branch',
      accessorKey: 'branchDisplay',
      size: 180,
    },
    {
      id: 'customerId',
      header: 'Customer ID',
      accessorKey: 'customerId',
      size: 130,
    },
    {
      id: 'customerName',
      header: 'Customer Name',
      accessorKey: 'customerName',
      size: 180,
    },
    {
      id: 'addonLoanType',
      header: 'Loan Type',
      accessorKey: 'addonLoanType',
      size: 130,
    },
    {
      id: 'addonLoanAccountNumber',
      header: 'Loan Account',
      accessorKey: 'addonLoanAccountNumber',
      size: 160,
    },
    {
      id: 'addonLoanDate',
      header: 'Loan Date',
      accessorKey: 'addonLoanDate',
      size: 120,
      cell: ({ getValue }: any) => fmtDate(getValue()) || '—',
    },
    {
      id: 'approvedAmount',
      header: 'Approved Amt (₹)',
      accessorKey: 'approvedAmount',
      size: 155,
      cell: ({ getValue }: any) => (
        <span style={{ fontWeight: 600 }}>{formatCurrency(getValue())}</span>
      ),
    },
    {
      id: 'statusCode',
      header: 'Status',
      accessorKey: 'statusCode',
      size: 140,
      cell: ({ getValue }: any) => (
        <span style={getStatusBadgeStyle(getValue())}>{(getValue() ?? '').replace(/_/g, ' ')}</span>
      ),
    },
    {
      id: 'glCount',
      header: 'GL Count',
      accessorKey: 'glCount',
      size: 100,
      cell: ({ getValue, row }: any) => {
        const count = getValue() as number;
        const rowId = (row.original as MappingRow).id;
        const isExpanded = expandedMappingId === rowId;
        return (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleGlCountClick(rowId);
            }}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 28,
              height: 24,
              padding: '0 8px',
              borderRadius: 12,
              border: 'none',
              cursor: 'pointer',
              fontSize: '0.75rem',
              fontWeight: 700,
              background: isExpanded ? 'var(--primary)' : 'var(--primary-light, #e8f0fe)',
              color: isExpanded ? '#fff' : 'var(--primary)',
              transition: 'background 0.15s, color 0.15s',
            }}
            title={isExpanded ? 'Collapse GL details' : 'View GL details'}
          >
            {count}
          </button>
        );
      },
    },
    {
      id: 'totalNetWeight',
      header: 'Eligible Weight (g)',
      accessorKey: 'totalNetWeight',
      size: 130,
      cell: ({ getValue }: any) => {
        const v = getValue() as number;
        return v > 0 ? <span style={{ fontWeight: 600 }}>{Number(v).toFixed(3)}</span> : '—';
      },
    },
    {
      id: 'totalGoldLoanAmount',
      header: 'Original GL Amt (₹)',
      accessorKey: 'totalGoldLoanAmount',
      size: 170,
      cell: ({ getValue }: any) => {
        const v = getValue() as number;
        return v > 0 ? <span style={{ fontWeight: 600 }}>{formatCurrency(v)}</span> : '—';
      },
    },
  ], [expandedMappingId, handleGlCountClick]);

  // ============================================================================
  // VIEW MODE & ACTION BUTTONS
  // ============================================================================

  const viewModeOptions: ViewModeOption[] = [
    { id: 'list', icon: <IconSystem.list size={16} />, label: 'List View', tooltip: 'List View' },
    { id: 'grid', icon: <IconSystem.grid size={16} />, label: 'Grid View', tooltip: 'Grid View', disabled: true },
  ];

  const isExporting = exportSummaryLoading || exportDetailLoading;

  const actionButtons: ActionButton[] = [
    {
      id: 'refresh',
      icon: <IconSystem.refresh size={18} />,
      label: 'Refresh',
      variant: 'icon',
      tooltip: 'Refresh',
      onClick: () => refetch(),
    },
    {
      id: 'export',
      icon: <IconSystem.download size={16} />,
      label: '',
      variant: 'icon',
      tooltip: isExporting ? 'Exporting…' : 'Export',
      onClick: () => { if (!isExporting) setExportMenuOpen((prev) => !prev); },
      disabled: isExporting,
    },
  ];

  const expandedRow = expandedMappingId ? mappingRows.find((r) => r.id === expandedMappingId) : null;

  // Shared dropdown item style builder
  const dropdownItemStyle = (loading: boolean): React.CSSProperties => ({
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    width: '100%',
    padding: '10px 16px',
    border: 'none',
    background: 'transparent',
    cursor: loading ? 'not-allowed' : 'pointer',
    fontSize: '0.85rem',
    color: loading ? 'var(--text-disabled, #aaa)' : 'var(--text-primary)',
    textAlign: 'left',
  });

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <div className="module-master-container" style={{ position: 'relative' }}>
      <ModuleHeader
        visible={true}
        title="Loan Mapping Report"
        icon={<IconSystem.list size={20} />}
        description="Mapping details with GL allocation breakdown and eligibility calculations."
        searchConfig={{ value: searchQuery, onChange: setSearchQuery, placeholder: 'Search mappings...' }}
        viewModeConfig={{ currentMode: viewMode, modes: viewModeOptions, onChange: (mode) => setViewMode(mode) }}
        filterConfig={{ onClick: () => setFilterOpen(!filterOpen), isActive: isFilterActive }}
        actions={actionButtons}
      />

      {/* Export dropdown menu */}
      {exportMenuOpen && (
        <div
          ref={exportMenuRef}
          style={{
            position: 'absolute',
            top: 52,
            right: 12,
            zIndex: 400,
            background: 'var(--surface, #fff)',
            border: '1px solid var(--border, #e0e0e0)',
            borderRadius: 8,
            boxShadow: '0 4px 20px rgba(0,0,0,0.13)',
            minWidth: 168,
            overflow: 'hidden',
          }}
        >
          {/* Summary */}
          <button
            onClick={handleExportSummary}
            disabled={exportSummaryLoading}
            style={dropdownItemStyle(exportSummaryLoading)}
            onMouseEnter={(e) => { if (!exportSummaryLoading) (e.currentTarget).style.background = 'var(--hover-bg, #f5f7fa)'; }}
            onMouseLeave={(e) => { (e.currentTarget).style.background = 'transparent'; }}
          >
            {exportSummaryLoading
              ? <ExportSpinner />
              : <IconSystem.download size={14} />
            }
            {exportSummaryLoading ? 'Exporting…' : 'Summary'}
          </button>

          <div style={{ height: 1, background: 'var(--border-light, #f0f0f0)', margin: '0 12px' }} />

          {/* Detail */}
          <button
            onClick={handleExportDetail}
            disabled={exportDetailLoading}
            style={dropdownItemStyle(exportDetailLoading)}
            onMouseEnter={(e) => { if (!exportDetailLoading) (e.currentTarget).style.background = 'var(--hover-bg, #f5f7fa)'; }}
            onMouseLeave={(e) => { (e.currentTarget).style.background = 'transparent'; }}
          >
            {exportDetailLoading
              ? <ExportSpinner />
              : <IconSystem.download size={14} />
            }
            {exportDetailLoading ? 'Exporting…' : 'Detail'}
          </button>
        </div>
      )}

      <div className="module-content">
        {apiError && data.length === 0 && (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '3rem', color: 'var(--danger)' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '0.9rem', marginBottom: '0.5rem' }}>Failed to load report data</div>
              <button className="btn btn-secondary" onClick={() => refetch()} style={{ fontSize: '0.8rem' }}>Retry</button>
            </div>
          </div>
        )}

        {apiLoading && data.length === 0 ? (
          <GridLoadingSkeleton columns={6} />
        ) : (
          <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>

            {/* Grid wrapper — shrinks to make room for GL panel */}
            <div style={{ flex: 1, overflow: 'hidden', minHeight: 0 }}>
              <SyutGrid
                data={mappingRows}
                columns={tableColumns}
                enableRowSelection={false}
                enablePagination
                manualPagination
                totalCount={pagination.totalItems}
                currentPage={(queryParams.page ?? 1) - 1}
                pageSize={queryParams.pageSize ?? DEFAULT_PAGE_SIZE}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                enableColumnFilters={false}
                isLoading={apiLoading}
                emptyMessage="No loan mappings match the current filters."
              />
            </div>

            {/* GL Detail Panel — fixed height, does not shrink */}
            {expandedMappingId && (
              <div style={{
                flexShrink: 0,
                borderTop: '2px solid var(--primary)',
                background: 'var(--surface, #fff)',
                padding: '0.75rem 1rem',
                maxHeight: 260,
                overflow: 'auto',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.6rem' }}>
                  <div>
                    <span style={{ fontWeight: 700, fontSize: '0.85rem', color: 'var(--primary)' }}>GL Details</span>
                    {expandedRow && (
                      <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginLeft: '0.5rem' }}>
                        {'—'} {expandedRow.customerName}
                        {expandedRow.addonLoanAccountNumber ? ` (${expandedRow.addonLoanAccountNumber})` : ''}
                      </span>
                    )}
                  </div>
                  <button
                    className="search-panel-close"
                    onClick={() => setExpandedMappingId(null)}
                    title="Close GL details"
                  >
                    <IconSystem.x size={14} />
                  </button>
                </div>

                {glPanelLoading ? (
                  <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
                    Loading GL details…
                  </div>
                ) : glPanelRows.length === 0 ? (
                  <div style={{ padding: '0.5rem', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
                    No GL records found for this mapping.
                  </div>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                    <thead>
                      <tr style={{ background: 'var(--table-header-bg, #f4f6f9)' }}>
                        {['GL Account Number', 'GL Date', 'GL Customer ID', 'GL Customer Name', 'Eligible Weight (g)', 'Original Loan Amt (₹)', 'Alloc. Ratio (%)', 'GL Status'].map((h) => (
                          <th key={h} style={{ padding: '6px 10px', textAlign: 'left', fontWeight: 600, color: 'var(--text-secondary)', whiteSpace: 'nowrap', borderBottom: '1px solid var(--border)' }}>
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {glPanelRows.map((gl) => (
                        <tr key={gl.id} style={{ borderBottom: '1px solid var(--border-light, #eee)' }}>
                          <td style={{ padding: '6px 10px' }}>{gl.glNumber || '—'}</td>
                          <td style={{ padding: '6px 10px' }}>{fmtDate(gl.loanDate) || '—'}</td>
                          <td style={{ padding: '6px 10px' }}>{gl.glCustomerId || '—'}</td>
                          <td style={{ padding: '6px 10px' }}>{gl.glCustomerName || '—'}</td>
                          <td style={{ padding: '6px 10px', fontWeight: 600 }}>{Number(gl.netWeight).toFixed(3)}</td>
                          <td style={{ padding: '6px 10px', fontWeight: 600 }}>{formatCurrency(gl.originalLoanAmount)}</td>
                          <td style={{ padding: '6px 10px', fontWeight: 600 }}>{(Number(gl.allocationRatio) * 100).toFixed(2)}%</td>
                          <td style={{ padding: '6px 10px' }}>
                            <span style={getStatusBadgeStyle(gl.statusCode ?? '')}>{(gl.statusCode ?? '').replace(/_/g, ' ')}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>
        )}

        {/* Filter Panel */}
        <div className={`search-panel ${filterOpen ? '' : 'collapsed'}`}>
          <div className="search-panel-header">
            <h3 className="search-panel-title">Filters</h3>
            <button className="search-panel-close" onClick={() => setFilterOpen(false)} title="Close filters">
              <IconSystem.x size={14} />
            </button>
          </div>

          <div className="search-panel-content">
            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.75rem' }}>Status</label>
              <select
                className="form-input"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                style={{ fontSize: '0.8rem' }}
              >
                <option value="">All</option>
                <option value="OPEN">Open</option>
                <option value="CLOSED">Closed</option>
                <option value="PENDING_APPROVAL">Pending Approval</option>
                <option value="APPROVED">Approved</option>
              </select>
            </div>

            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.75rem' }}>Branch</label>
              <input
                type="text"
                className="form-input"
                placeholder="Filter by branch code or name..."
                value={filterBranch}
                onChange={(e) => setFilterBranch(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleApplyFilters(); }}
                style={{ fontSize: '0.8rem' }}
              />
            </div>

            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.75rem' }}>Customer ID</label>
              <input
                type="text"
                className="form-input"
                placeholder="Filter by customer ID..."
                value={filterCustomerId}
                onChange={(e) => setFilterCustomerId(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleApplyFilters(); }}
                style={{ fontSize: '0.8rem' }}
              />
            </div>

            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.75rem' }}>Loan Account</label>
              <input
                type="text"
                className="form-input"
                placeholder="Filter by loan account..."
                value={filterLoanAccount}
                onChange={(e) => setFilterLoanAccount(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleApplyFilters(); }}
                style={{ fontSize: '0.8rem' }}
              />
            </div>

            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.75rem' }}>GL Account Number</label>
              <input
                type="text"
                className="form-input"
                placeholder="Filter by GL number..."
                value={filterGlNumber}
                onChange={(e) => setFilterGlNumber(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleApplyFilters(); }}
                style={{ fontSize: '0.8rem' }}
              />
            </div>

            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.75rem' }}>GL Customer ID</label>
              <input
                type="text"
                className="form-input"
                placeholder="Filter by GL customer ID..."
                value={filterGlCustomerId}
                onChange={(e) => setFilterGlCustomerId(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleApplyFilters(); }}
                style={{ fontSize: '0.8rem' }}
              />
            </div>

            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.75rem' }}>GL Customer Name</label>
              <input
                type="text"
                className="form-input"
                placeholder="Filter by GL customer name..."
                value={filterGlCustomerName}
                onChange={(e) => setFilterGlCustomerName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleApplyFilters(); }}
                style={{ fontSize: '0.8rem' }}
              />
            </div>

            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.75rem' }}>Date From</label>
              <input
                type="date"
                className="form-input"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                style={{ fontSize: '0.8rem' }}
              />
            </div>

            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.75rem' }}>Date To</label>
              <input
                type="date"
                className="form-input"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                style={{ fontSize: '0.8rem' }}
              />
            </div>
          </div>

          <div className="filter-actions">
            <button className="btn btn-secondary" onClick={handleClearFilters}>Clear</button>
            <button className="btn btn-primary" onClick={handleApplyFilters}>Apply</button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// SPINNER — animated SVG used inside export dropdown items
// ============================================================================

const ExportSpinner: React.FC = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 14 14"
    style={{ animation: 'lmms-spin 0.8s linear infinite', flexShrink: 0 }}
  >
    <style>{`@keyframes lmms-spin { to { transform: rotate(360deg); } }`}</style>
    <circle cx="7" cy="7" r="5.5" fill="none" stroke="var(--primary, #1E3A5F)" strokeWidth="2" strokeDasharray="22 10" strokeLinecap="round" />
  </svg>
);

export default LoanMappingReportScreen;
